<script lang="ts">
  // AvatarPicker — modal full-screen com hero "PILOT REGISTRY"
  // + carrossel horizontal de pilotos. Visual inspirado no vanilla.
  // Click optimista atualiza local + PUT /users/me/avatar; reverte
  // em caso de erro.
  import { avatar, userName } from '../../stores/auth';
  import { api, ApiError } from '../../lib/api';

  type Props = { open: boolean; onClose: () => void };
  let { open, onClose }: Props = $props();

  const CHARS: string[] = Array.from({ length: 16 }, (_, i) => `character${i + 1}`);

  let saving = $state(false);
  let errorMsg = $state('');

  async function pick(key: string) {
    if (key === $avatar || saving) return;
    const previous = $avatar;
    avatar.set(key);
    errorMsg = '';
    saving = true;
    try {
      await api.put('/users/me/avatar', { avatar: key });
    } catch (e) {
      avatar.set(previous);
      errorMsg = e instanceof ApiError ? `Falhou: ${e.message}` : String(e);
    } finally {
      saving = false;
    }
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && open) onClose();
  }

</script>

<svelte:window onkeydown={onKeydown} />

{#if open}
  <div
    class="overlay"
    role="dialog"
    aria-modal="true"
    aria-label="Escolher avatar"
    onclick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    onkeydown={(e) => { if (e.key === 'Escape' && e.target === e.currentTarget) onClose(); }}
    tabindex="-1"
  >
    <div class="card">
      <header class="close-bar">
        <button class="close" onclick={onClose} aria-label="fechar">✕</button>
      </header>

      <!-- Hero: avatar grande + nome + status -->
      <div class="hero">
        <div class="hero-avatar-wrap">
          <img class="hero-avatar" src={`/images/${$avatar}.webp`} alt={$avatar} />
          <div class="hero-glow" aria-hidden="true"></div>
        </div>
        <div class="hero-meta">
          <p class="eyebrow">::: seu perfil</p>
          <h1 class="hero-name">{$userName || 'jogador'}</h1>
          <div class="chips">
            <span class="chip online">
              <span class="dot"></span>
              online
            </span>
          </div>
        </div>
      </div>

      <!-- Carrossel: SELECIONE SEU AVATAR + ARRASTE -->
      <div class="picker">
        <div class="picker-head">
          <span class="picker-title">SELECIONE SEU AVATAR</span>
          <span class="picker-hint">arraste →</span>
        </div>
        <div class="scroller">
          {#each CHARS as key (key)}
            <button
              type="button"
              class="slot"
              class:selected={$avatar === key}
              disabled={saving}
              onclick={() => pick(key)}
              aria-label={`Selecionar ${key}`}
            >
              <img src={`/images/${key}.webp`} alt={key} loading="lazy" />
              {#if $avatar === key}
                <span class="check" aria-hidden="true">✓</span>
              {/if}
            </button>
          {/each}
        </div>
      </div>

      {#if errorMsg}
        <div class="error" role="alert">{errorMsg}</div>
      {/if}

      <footer>
        <button class="done" onclick={onClose}>pronto</button>
      </footer>
    </div>
  </div>
{/if}

<style>
  .overlay {
    position: fixed;
    inset: 0;
    z-index: 1500;
    display: grid;
    place-items: start center;
    padding: 16px;
    overflow-y: auto;
    background: rgba(2, 3, 26, 0.85);
    backdrop-filter: blur(6px);
    animation: fade 0.2s ease-out;
  }
  @keyframes fade { from { opacity: 0; } to { opacity: 1; } }

  /* Card cartoon — mesmo esquema do TournamentDetailModal: creme com
     borda azul forte e outline amarelo. Substitui o tema roxo cyber. */
  .card {
    width: 100%;
    max-width: 580px;
    margin-top: 32px;
    margin-bottom: 32px;
    background: linear-gradient(180deg, #faf5ec 0%, #fff 60%, #f0e7d5 100%);
    border: 4px solid #0D47A1;
    outline: 2px solid #ffb300;
    outline-offset: -3px;
    border-radius: 14px;
    box-shadow: 0 6px 0 #051a3a, 0 18px 50px rgba(0, 0, 0, 0.5);
    overflow: hidden;
    animation: pop 0.25s cubic-bezier(0.2, 1.2, 0.4, 1);
  }
  @keyframes pop {
    from { transform: scale(0.96); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }

  .close-bar {
    display: flex;
    justify-content: flex-end;
    padding: 10px 12px 0;
  }
  .close {
    width: 32px;
    height: 32px;
    padding: 0;
    font-size: 18px;
    font-weight: 900;
    line-height: 1;
    background: #fff;
    border: 2px solid #1565C0;
    color: #1565C0;
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 2px 0 #0D47A1;
    transition: all 0.1s;
  }
  .close:hover { background: #e3f2fd; transform: translateY(-1px); box-shadow: 0 3px 0 #0D47A1; }
  .close:active { transform: translateY(1px); box-shadow: 0 1px 0 #0D47A1; }

  /* Hero — avatar grande, name, chips. Versao cartoon: card claro com
     borda azul, glow amarelo no avatar (combina com a borda do topbar). */
  .hero {
    display: flex;
    align-items: center;
    gap: 18px;
    margin: 4px 16px 18px;
    padding: 18px;
    border-radius: 12px;
    background:
      radial-gradient(circle at 22% 40%, rgba(255, 210, 74, 0.22), transparent 55%),
      linear-gradient(180deg, #faf5ec 0%, #fff 60%, #f0e7d5 100%);
    border: 3px solid #1565C0;
    box-shadow: 0 3px 0 #0D47A1, inset 0 1px 0 rgba(255, 255, 255, 0.6);
    overflow: hidden;
    position: relative;
  }
  .hero-avatar-wrap {
    position: relative;
    flex: 0 0 auto;
    width: 96px;
    height: 96px;
  }
  .hero-avatar {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #ffd24a;
    box-shadow:
      0 0 0 3px #1565C0,
      0 4px 0 #0D47A1,
      0 6px 16px rgba(13, 71, 161, 0.35);
    background: #faf5ec;
    position: relative;
    z-index: 1;
  }
  .hero-glow {
    position: absolute;
    inset: -10px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 210, 74, 0.35), transparent 70%);
    pointer-events: none;
    animation: glow-pulse 2.4s ease-in-out infinite;
  }
  @keyframes glow-pulse {
    0%, 100% { transform: scale(1); opacity: 0.55; }
    50% { transform: scale(1.15); opacity: 0.9; }
  }

  .hero-meta {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .eyebrow {
    margin: 0;
    color: #1976D2;
    font-size: 10px;
    letter-spacing: 3px;
    text-transform: uppercase;
    font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
  }
  .hero-name {
    margin: 0;
    font-size: 28px;
    font-weight: 900;
    color: #0D47A1;
    letter-spacing: 0.5px;
    line-height: 1.1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 999px;
    border: 2px solid #1565C0;
    background: #fff;
    font-size: 10px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #0D47A1;
    font-weight: 800;
    box-shadow: 0 1px 0 #0D47A1;
  }
  .chip.online { color: #1b5e20; border-color: #2e7d32; }
  .chip.online .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #2e7d32;
    box-shadow: 0 0 6px rgba(46, 125, 50, 0.7);
  }
  .chip.char { color: #b85a00; border-color: #ffb300; background: #fff8e1; }

  /* Carrossel — versao cartoon: bg creme, borda azul, fades em creme. */
  .picker {
    position: relative;
    margin: 0 16px 14px;
    padding: 10px 0 12px;
    background: linear-gradient(180deg, #faf5ec 0%, #fff 100%);
    border: 3px solid #1565C0;
    border-radius: 12px;
    box-shadow: 0 3px 0 #0D47A1;
  }
  .picker-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 0 16px 10px;
  }
  .picker-title {
    font-size: 11px;
    letter-spacing: 2.5px;
    text-transform: uppercase;
    color: #0D47A1;
    font-weight: 900;
  }
  .picker-hint {
    font-size: 9px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #1976D2;
    font-weight: 700;
  }

  .scroller {
    display: flex;
    gap: 10px;
    padding: 4px 14px 8px;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: #1565C0 transparent;
  }
  .scroller::-webkit-scrollbar { height: 6px; }
  .scroller::-webkit-scrollbar-track { background: transparent; }
  .scroller::-webkit-scrollbar-thumb {
    background: #1565C0;
    border-radius: 3px;
  }

  /* Fade edges (creme pra combinar com o fundo do carrossel) */
  .picker::before,
  .picker::after {
    content: '';
    position: absolute;
    top: 38px;
    bottom: 14px;
    width: 28px;
    pointer-events: none;
    z-index: 2;
  }
  .picker::before {
    left: 1px;
    background: linear-gradient(90deg, #faf5ec 0%, rgba(250, 245, 236, 0) 100%);
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
  }
  .picker::after {
    right: 1px;
    background: linear-gradient(270deg, #faf5ec 0%, rgba(250, 245, 236, 0) 100%);
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
  }

  .slot {
    position: relative;
    flex: 0 0 auto;
    width: 76px;
    height: 76px;
    padding: 0;
    border-radius: 14px;
    border: 2.5px solid #1565C0;
    background: linear-gradient(180deg, #faf5ec 0%, #fff 100%);
    box-shadow: 0 2px 0 #0D47A1;
    overflow: hidden;
    cursor: pointer;
    scroll-snap-align: center;
    transition: transform 0.18s, border-color 0.18s, box-shadow 0.18s;
  }
  .slot:hover:not(:disabled) {
    border-color: #ffb300;
    transform: translateY(-3px);
    box-shadow: 0 4px 0 #b85a00;
  }
  .slot:disabled { opacity: 0.6; cursor: wait; }
  .slot.selected {
    border-color: #2e7d32;
    box-shadow:
      0 2px 0 #1b5e20,
      0 0 14px rgba(46, 125, 50, 0.45),
      inset 0 0 10px rgba(46, 125, 50, 0.15);
  }
  .slot img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .check {
    position: absolute;
    bottom: 4px;
    right: 4px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: linear-gradient(180deg, #66bb6a, #43a047);
    color: #fff;
    display: grid;
    place-items: center;
    font-weight: 900;
    font-size: 12px;
    box-shadow: 0 2px 0 #1b5e20, 0 2px 8px rgba(46, 125, 50, 0.5);
    border: 1.5px solid #1b5e20;
  }

  .error {
    margin: 0 16px 12px;
    color: #ad1457;
    background: rgba(255, 76, 126, 0.12);
    border: 2px solid #c2185b;
    border-radius: 10px;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 700;
  }

  footer {
    padding: 6px 16px 16px;
  }
  /* PRONTO — botao cartoon verde (mesmo gradient/sombra do status-finished
     em torneios). Confirma a acao com cara de "ok!". */
  .done {
    width: 100%;
    background: linear-gradient(180deg, #66bb6a, #43a047);
    color: #fff;
    border: 2px solid #1b5e20;
    font-weight: 900;
    letter-spacing: 2px;
    text-transform: uppercase;
    padding: 13px;
    border-radius: 10px;
    cursor: pointer;
    box-shadow: 0 3px 0 #1b5e20, inset 0 1px 0 rgba(255, 255, 255, 0.25);
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.25);
    transition: transform 0.1s, box-shadow 0.1s, filter 0.1s;
  }
  .done:hover { filter: brightness(1.05); }
  .done:active { transform: translateY(2px); box-shadow: 0 1px 0 #1b5e20; }

  @media (max-width: 480px) {
    .hero { flex-direction: column; align-items: flex-start; gap: 14px; }
    .hero-name { font-size: 22px; }
    .slot { width: 68px; height: 68px; }
  }
</style>
